import { View } from 'app/shared/collections/view';
import { IMSVisualiser } from 'app/views/visualiser';
import { MapOption } from 'app/shared/map_options/MapOptionsService';
import { ASSET_DISPLAY_COLOUR } from 'app/views/BaseViewController';
import {
    InitialMapOptionsSetter,
    ReturnedSelectedMapOptions,
} from 'app/shared/map_options/InitialMapOptions';
import { merge, omit } from 'lodash';
import { ILocalStorageService } from 'angular-local-storage';

interface Option {
    key: string;
    value: any;
    panelKey: string;
}

export interface Component {
    config: Array<Option>;
    type: string;
}

export interface AvailableMapOptions {
    [key: string]: MapOption;
}

export interface StoredMapOptions {
    [key: string]: string;
}

interface AssetImsVisualisers {
    [key: string]: IMSVisualiser;
}

/**
 * This class is here mainly to provide extra validation for assetDisplayColor which can cause everything to explode if the field
 * is set to something without a proper visualiser.
 */
export class InitialDefaultMapOptionsForAsset extends InitialMapOptionsSetter {
    protected _localStorageService: ILocalStorageService;
    protected _panelKey: string;
    protected _availableMapOptions: AvailableMapOptions;
    private _assetImsVisualisers: AssetImsVisualisers;

    constructor(
        localStorageService: ILocalStorageService,
        view: View,
        panelKey: string,
        assetImsVisualisers: { [key: string]: IMSVisualiser },
    ) {
        super(localStorageService, panelKey, view);
        this._assetImsVisualisers = assetImsVisualisers;
    }

    protected _getInitialDefaultMapOptions(defaultMapOptions: {
        [key: string]: string;
    }): ReturnedSelectedMapOptions {
        const optionsFromLocalStorage = this._getDefaultsFromLocalStorage();
        const validatedOptionsLocStorage = _includesAssetDisplayColour(optionsFromLocalStorage)
            ? this._validateAssetDisplayColorLocalStorage(optionsFromLocalStorage)
            : optionsFromLocalStorage;

        const mapOptionsFromApi = this._getDefaultsFromViewComponent();
        const validatedMapOptionsFromApi = _includesAssetDisplayColour(mapOptionsFromApi)
            ? this._validateAssetDisplayColorAPI(mapOptionsFromApi)
            : mapOptionsFromApi;

        const defaultSelectedMapOptions = merge(
            {},
            defaultMapOptions,
            validatedMapOptionsFromApi,
            validatedOptionsLocStorage,
        );

        const selectedMapOptions = Object.assign({}, defaultSelectedMapOptions);
        return { defaultSelectedMapOptions, selectedMapOptions };
    }

    //these two methods are very similar. They're separated due to the console warning
    //which exists because of how easy and annoying it is to typo a key in the API
    private _validateAssetDisplayColorAPI(mapOptions: StoredMapOptions): StoredMapOptions {
        if (_assetDisplayColorValid(this._assetImsVisualisers, mapOptions)) {
            return mapOptions;
        } else {
            console.warn(
                `invalid view component config in the API! the key ${ASSET_DISPLAY_COLOUR} has the invalid visualiser type ${
                    mapOptions[ASSET_DISPLAY_COLOUR] + 'IMS will auto select a default'
                }`,
            );
        }
        return omit(mapOptions, ASSET_DISPLAY_COLOUR);
    }

    private _validateAssetDisplayColorLocalStorage(mapOptions: StoredMapOptions): StoredMapOptions {
        if (_assetDisplayColorValid(this._assetImsVisualisers, mapOptions)) {
            return mapOptions;
        } else {
            return omit(mapOptions, ASSET_DISPLAY_COLOUR);
        }
    }
}

function _includesAssetDisplayColour(mapOptionsFromApi: { [key: string]: string }): boolean {
    return !!mapOptionsFromApi && !!mapOptionsFromApi[ASSET_DISPLAY_COLOUR];
}

function _assetDisplayColorValid(
    assetImsVisualisers: { [key: string]: IMSVisualiser },
    storedMapOption: { [key: string]: string },
): boolean {
    return Object.keys(assetImsVisualisers).includes(storedMapOption[ASSET_DISPLAY_COLOUR]);
}
